import { useCallback } from 'react';

import { useActorRef } from '@xstate5/react';

import {
  getLocationPermissionStatusActor,
  getPushNotificationPermissionStatusActor,
  requestLocationPermissionActor,
  requestPushNotificationPermissionActor,
} from './actors';
import { permissionsMachine, type PermissionsMachineImplementations } from './permissions.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitPermissionsMachine = (params: UseInitPermissionsMachineParams) => {
  const { isPushNotificationPrimerEnabled, isLocationPrimerEnabled, implementations } = params;

  // ─── Guards ──────────────────────────────────────────────────────────

  const checkIfPushNotificationPrimerEnabled = useCallback(
    () => isPushNotificationPrimerEnabled ?? false,
    [isPushNotificationPrimerEnabled],
  );

  const checkIfLocationPrimerEnabled = useCallback(
    () => isLocationPrimerEnabled,
    [isLocationPrimerEnabled],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useActorRef(
    permissionsMachine.provide({
      ...implementations,
      actors: {
        ...implementations?.actors,
        getPushNotificationPermissionStatus: getPushNotificationPermissionStatusActor,
        requestPushNotificationPermission: requestPushNotificationPermissionActor,
        getLocationPermissionStatus: getLocationPermissionStatusActor,
        requestLocationPermission: requestLocationPermissionActor,
      },
      guards: {
        ...implementations?.guards,
        checkIfPushNotificationPrimerEnabled,
        checkIfLocationPrimerEnabled,
      },
      actions: {
        ...implementations?.actions,
      },
    }),
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseInitPermissionsMachineParams = {
  isPushNotificationPrimerEnabled?: boolean;
  isLocationPrimerEnabled: boolean;
  implementations?: PermissionsMachineImplementations;
};
